import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { fmtDateTime, fmtDate as fmtPtDate } from '@/utils/dateFmt';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { CaretLeft, CaretRight, Plus, Trash, Clock, User, Copy, ArrowSquareOut, Warning } from '@phosphor-icons/react';
import { WhatsAppConfirmButton } from '@/components/admin/WhatsAppConfirmButton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import PatientSearch from '@/components/PatientSearch';
import { ReserveSlotDialog, HoldDetailDialog } from '@/components/admin/CalendarHoldDialogs';
import { ConflictReportDialog } from '@/components/admin/ConflictReportDialog';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';
import { useConfirmDelete } from '@/components/ConfirmDialogs';

const API = process.env.REACT_APP_BACKEND_URL;
const HOURS = Array.from({ length: 14 }, (_, i) => i + 7); // 7:00 to 20:00
const DAY_NAMES = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const MONTH_NAMES = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

function getWeekDates(date) {
  const d = new Date(date);
  const day = d.getDay();
  const mon = new Date(d); mon.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
  return Array.from({ length: 7 }, (_, i) => { const dd = new Date(mon); dd.setDate(mon.getDate() + i); return dd; });
}

// Local YYYY-MM-DD (avoids toISOString UTC shift that moved month cells back a day in UTC+ timezones)
function fmtDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function CalendarioPage() {
  useRequireAdmin();
  const { user } = useAuth();
  const confirmDelete = useConfirmDelete();
  const isAdmin = user?.role === 'admin';
  const [currentDate, setCurrentDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [events, setEvents] = useState([]);
  const [clients, setClients] = useState([]);
  const [team, setTeam] = useState([]);
  const [holds, setHolds] = useState([]);
  const [reserveOpen, setReserveOpen] = useState(false);
  const [conflictsOpen, setConflictsOpen] = useState(false);
  const [holdDetail, setHoldDetail] = useState(null);
  const [view, setView] = useState('week'); // week | month | day
  const [showAdd, setShowAdd] = useState(false);
  const [addType, setAddType] = useState('consulta');
  const [editingAptId, setEditingAptId] = useState(null);
  const [originalLink, setOriginalLink] = useState('');
  const [form, setForm] = useState({ patient_id: '', date: '', time: '09:00', duration: 60, type: 'consulta', title: '', description: '', color: '#657161', tags: [], nutritionist_id: user?.user_id || '' });
  const [allTags, setAllTags] = useState([]);
  const [showTagMgmt, setShowTagMgmt] = useState(false);
  const [newTag, setNewTag] = useState({ name: '', color: '#657161' });
  const [showCancel, setShowCancel] = useState(null); // {id, patient_name, date, time}
  const [cancelReason, setCancelReason] = useState('');
  const [cancelMessage, setCancelMessage] = useState('');
  const [showReschedule, setShowReschedule] = useState(null);
  const [reschedForm, setReschedForm] = useState({ new_date: '', new_time: '09:00', reason: '' });
  // PROMPT 3 — Item 3: matches da lista de espera quando um slot é libertado/aberto
  const [waitlistMatches, setWaitlistMatches] = useState(null); // { date, time, modality, program, matches: [...] }
  const [matchesLoading, setMatchesLoading] = useState(false);

  const fetchWaitlistMatches = useCallback(async ({ date, time, modality, program }) => {
    if (!date) return;
    setMatchesLoading(true);
    try {
      const q = new URLSearchParams({ date, time: time || '', modality: modality || 'indiferente', program: program || '' }).toString();
      const { data } = await axios.get(`${API}/api/admin/waitlist/matches?${q}`);
      setWaitlistMatches(data);
    } catch { /* silencioso */ } finally { setMatchesLoading(false); }
  }, []);

  const weekDates = useMemo(() => getWeekDates(currentDate), [currentDate]);
  const weekStart = fmtDate(weekDates[0]);
  const weekEnd = fmtDate(weekDates[6]);

  // Month grid (always starts on Monday of week containing day 1 and ends Sunday of week containing last day)
  const monthGrid = useMemo(() => {
    const y = currentDate.getFullYear();
    const m = currentDate.getMonth();
    const first = new Date(y, m, 1);
    const last = new Date(y, m + 1, 0);
    const firstDay = first.getDay(); // 0=Sun
    const startOffset = firstDay === 0 ? 6 : firstDay - 1;
    const start = new Date(first); start.setDate(first.getDate() - startOffset);
    const cells = [];
    const total = Math.ceil((startOffset + last.getDate()) / 7) * 7;
    for (let i = 0; i < total; i++) {
      const d = new Date(start); d.setDate(start.getDate() + i);
      cells.push(d);
    }
    return cells;
  }, [currentDate]);
  const monthRangeStart = monthGrid[0] ? fmtDate(monthGrid[0]) : undefined;
  const monthRangeEnd = monthGrid[monthGrid.length - 1] ? fmtDate(monthGrid[monthGrid.length - 1]) : undefined;

  const rangeStart = view === 'month' ? monthRangeStart : weekStart;
  const rangeEnd = view === 'month' ? monthRangeEnd : weekEnd;

  const fetch = useCallback(async () => {
    try {
      const [aRes, eRes, cRes, tRes, teamRes, hRes] = await Promise.all([
        axios.get(`${API}/api/appointments/upcoming?date_from=${rangeStart}&date_to=${rangeEnd}`),
        axios.get(`${API}/api/calendar-events?date_from=${rangeStart}&date_to=${rangeEnd}`),
        axios.get(`${API}/api/patients`).catch(() => ({ data: [] })),
        axios.get(`${API}/api/calendar-tags`),
        isAdmin ? axios.get(`${API}/api/team`).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        axios.get(`${API}/api/admin/schedule-holds?date_from=${rangeStart}&date_to=${rangeEnd}`).catch(() => ({ data: [] })),
      ]);
      setHolds(Array.isArray(hRes.data) ? hRes.data : []);
      setAppointments(aRes.data || []);
      setEvents(eRes.data || []);
      setClients(Array.isArray(cRes.data) ? cRes.data : []);
      setAllTags(tRes.data || []);
      setTeam(Array.isArray(teamRes.data) ? teamRes.data : []);
    } catch {}
  }, [rangeStart, rangeEnd, isAdmin]);
  useEffect(() => { fetch(); }, [fetch]);
  useEffect(() => {
    const tick = async () => {
      if (document.hidden) return;
      try {
        const [aRes, eRes, hRes] = await Promise.all([
          axios.get(`${API}/api/appointments/upcoming?date_from=${rangeStart}&date_to=${rangeEnd}`),
          axios.get(`${API}/api/calendar-events?date_from=${rangeStart}&date_to=${rangeEnd}`),
          axios.get(`${API}/api/admin/schedule-holds?date_from=${rangeStart}&date_to=${rangeEnd}`).catch(() => ({ data: [] })),
        ]);
        setAppointments(aRes.data || []); setEvents(eRes.data || []); setHolds(Array.isArray(hRes.data) ? hRes.data : []);
      } catch {}
    };
    const t = setInterval(tick, 30000);
    document.addEventListener('visibilitychange', tick);
    window.addEventListener('focus', tick);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', tick); window.removeEventListener('focus', tick); };
  }, [rangeStart, rangeEnd]);

  const { search } = useLocation();
  const [focusApt, setFocusApt] = useState(null);
  useEffect(() => {
    const q = new URLSearchParams(search);
    const data = q.get('data');
    if (!data || !/^\d{4}-\d{2}-\d{2}$/.test(data)) return;
    const [y, m, d] = data.split('-').map(Number);
    setView(v => (v === 'month' ? 'week' : v));
    setCurrentDate(new Date(y, m - 1, d));
    setFocusApt({ date: data, time: q.get('hora') || '', id: q.get('apt') || '' });
  }, [search]);
  useEffect(() => {
    if (!focusApt) return;
    const el = (focusApt.id && document.querySelector(`[data-testid="apt-${focusApt.id}"]`)) || document.querySelector(`[data-slot="${focusApt.date}|${focusApt.time}"]`);
    if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); const t = setTimeout(() => setFocusApt(null), 6000); return () => clearTimeout(t); }
  }, [focusApt, appointments, view]);

  const navigate = (dir) => {
    const d = new Date(currentDate);
    if (view === 'month') d.setMonth(d.getMonth() + dir);
    else d.setDate(d.getDate() + (dir * 7));
    setCurrentDate(d);
  };
  const goToday = () => setCurrentDate(new Date());

  const saveEvent = async () => {
    try {
      if (addType === 'consulta') {
        const payload = { patient_id: form.patient_id, date: form.date, time: form.time, duration: parseInt(form.duration) || 60, type: form.type, notes: form.description };
        if (isAdmin) payload.nutritionist_id = form.nutritionist_id;
        if (editingAptId) {
          const finalLink = form.type === 'online' ? (form.link || '') : '';
          if (finalLink && !/^https:\/\//i.test(finalLink)) { toast.error('O link da videochamada tem de começar por https://'); return; }
          if (form.type === 'online' && originalLink && !finalLink) {
            if (!window.confirm('Tem a certeza que quer guardar a consulta sem link da videochamada?')) return;
          }
          payload.link = finalLink;
          await axios.put(`${API}/api/appointments/${editingAptId}`, payload);
          toast.success('Agendamento actualizado');
        } else {
          await axios.post(`${API}/api/appointments/quick`, payload);
          toast.success('Consulta agendada');
        }
      } else {
        await axios.post(`${API}/api/calendar-events`, { title: addType === 'bloco' ? (form.title || 'Indisponível') : form.title, date: form.date, time: form.time, end_time: form.end_time, description: form.description, color: addType === 'bloco' ? '#9CA3AF' : form.color, event_type: addType, is_block: addType === 'bloco' });
        toast.success(addType === 'bloco' ? 'Bloco criado' : 'Evento criado');
        // PROMPT 3 — Item 3: ao abrir disponibilidade, sugere candidatas da lista de espera
        if (addType !== 'bloco' && form.date) {
          fetchWaitlistMatches({ date: form.date, time: form.time, modality: form.modality || 'indiferente', program: form.program || '' });
        }
      }
      setShowAdd(false); setEditingAptId(null); fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  const deleteApt = async (id) => {
    const apt = appointments.find(a => a.id === id);
    const ok = await confirmDelete({ description: `Vai apagar a marcação de ${apt?.patient_name || ''} de ${fmtDateTime(apt?.date, apt?.time)}.` });
    if (!ok) return;
    await axios.delete(`${API}/api/appointments/${id}`); toast.success('Eliminada'); fetch();
  };
  const deleteEvent = async (id) => {
    const evt = events.find(e => e.id === id);
    const ok = await confirmDelete({ description: `Vai apagar o evento "${evt?.title || ''}" de ${fmtDateTime(evt?.date, '')}.` });
    if (!ok) return;
    await axios.delete(`${API}/api/calendar-events/${id}`); toast.success('Eliminado'); fetch();
  };

  const submitCancel = async () => {
    if (!cancelReason.trim()) { toast.error('Motivo interno obrigatório'); return; }
    try {
      await axios.post(`${API}/api/appointments/${showCancel.id}/cancel`, { reason: cancelReason.trim(), patient_message: cancelMessage.trim() });
      toast.success('Consulta cancelada');
      // PROMPT 3 — Item 3: ao cancelar uma consulta, sugere candidatas para o slot
      const apt = appointments.find(a => a.id === showCancel.id);
      if (apt) {
        fetchWaitlistMatches({ date: apt.date, time: apt.time, modality: /online/i.test(apt.type || apt.modality || '') ? 'online' : (/presen/i.test(apt.type || apt.modality || '') ? 'presencial' : 'indiferente'), program: apt.program || '' });
      }
      setShowCancel(null); setCancelReason(''); setCancelMessage(''); fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };
  const submitReschedule = async () => {    if (!reschedForm.new_date || !reschedForm.new_time) { toast.error('Nova data e hora obrigatórias'); return; }
    if (!reschedForm.reason.trim()) { toast.error('Motivo obrigatório'); return; }
    try {
      await axios.post(`${API}/api/appointments/${showReschedule.id}/reschedule`, {
        new_date: reschedForm.new_date, new_time: reschedForm.new_time, reason: reschedForm.reason.trim()
      });
      toast.success('Consulta remarcada');
      setShowReschedule(null); setReschedForm({ new_date: '', new_time: '09:00', reason: '' }); fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  const markNoShow = async (id, isFalta) => {
    try {
      await axios.post(`${API}/api/appointments/${id}/no-show${isFalta ? '/undo' : ''}`);
      toast.success(isFalta ? 'Falta revertida' : 'Consulta marcada como falta');
      setShowAdd(false); fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  const dismissGcalReview = async (id) => {
    try {
      await axios.post(`${API}/api/appointments/${id}/gcal-review/dismiss`);
      toast.success('Aviso dispensado');
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  const openAdd = (date, time) => {
    setEditingAptId(null);
    setOriginalLink('');
    setForm({ patient_id: '', date: date || fmtDate(new Date()), time: time || '09:00', duration: 60, type: 'consulta', title: '', description: '', color: '#657161', end_time: '', nutritionist_id: user?.user_id || '' });
    setAddType('consulta');
    setShowAdd(true);
  };

  const openEditApt = (apt) => {
    setEditingAptId(apt.id);
    setOriginalLink(apt.link || '');
    setForm({
      patient_id: apt.patient_id || '',
      date: apt.date || '',
      time: apt.time || '09:00',
      duration: apt.duration || 60,
      type: apt.type || 'consulta',
      link: apt.link || '',
      title: '',
      description: apt.notes || '',
      color: '#657161',
      end_time: '',
      nutritionist_id: apt.nutritionist_id || apt.created_by || user?.user_id || '',
      tags: [],
    });
    setAddType('consulta');
    setShowAdd(true);
  };

  // Merge appointments and events by day
  const PROG_COLORS = { gravida: '#A8D5D0', pos_parto: '#8BA88B', pre_concecao: '#D7BD7B', introducao_alimentar: '#F0B8A8', geral: '#B8BDB6', emagrecimento: '#C4B5D0' };
  const STATUS_OVERRIDE = { cancelled: '#9CA3AF', rescheduled: '#F59E0B', falta: '#DC2626' };
  const STATUS_LABEL = { cancelled: 'Cancelada', rescheduled: 'Remarcada', falta: 'Faltou' };
  const dayItems = (dateStr) => {
    const apts = appointments.filter(a => a.date === dateStr).map(a => {
      const client = clients.find(c => c.user_id === a.patient_id);
      const progColor = PROG_COLORS[client?.patient_type] || '#657161';
      const needsReview = !!a.needs_review && !STATUS_OVERRIDE[a.status];
      const color = STATUS_OVERRIDE[a.status] || (needsReview ? '#DC2626' : progColor);
      const statusLabel = STATUS_LABEL[a.status] || (needsReview ? 'Cancelada no Google Calendar' : (a.payment_pending_confirmation && !a.is_paid ? 'Pagamento por confirmar' : ''));
      return { ...a, _type: 'apt', _color: color, _label: `${a.time || ''} ${a.patient_name || ''}`.trim(), _time: a.time || '00:00', _statusLabel: statusLabel, _needsReview: needsReview };
    });
    const evts = events.filter(e => e.date === dateStr).map(e => ({ ...e, _type: 'event', _color: e.is_block ? '#9CA3AF' : (e.color || '#D7BD7B'), _label: e.is_block ? `${e.time || ''} ${e.title || 'Indisponível'}`.trim() : `${e.time || ''} ${e.title || ''}`.trim(), _time: e.time || '00:00', _isBlock: !!e.is_block }));
    const hs = holds.filter(h => h.date === dateStr).map(h => ({ ...h, _type: 'hold', _color: '#B7791F', _label: `${h.time} Reserva: ${h.patient_name}`, _time: h.time || '00:00' }));
    return [...apts, ...evts, ...hs].sort((a, b) => (a._time || '').localeCompare(b._time || ''));
  };

  const todayStr = fmtDate(new Date());

  return (
    <div className="fade-in" data-testid="calendario-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-brand-bg" data-testid="calendar-prev-btn"><CaretLeft size={18} className="text-brand-green" /></button>
          <h2 className="font-heading font-medium text-lg text-brand-text">
            {view === 'month' ? `${MONTH_NAMES[currentDate.getMonth()]} ${currentDate.getFullYear()}` : `${weekDates[0].getDate()} ${MONTH_NAMES[weekDates[0].getMonth()].slice(0,3)} a ${weekDates[6].getDate()} ${MONTH_NAMES[weekDates[6].getMonth()]} ${weekDates[6].getFullYear()}`}
          </h2>
          <button onClick={() => navigate(1)} className="p-2 rounded-full hover:bg-brand-bg" data-testid="calendar-next-btn"><CaretRight size={18} className="text-brand-green" /></button>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex bg-brand-bg border border-brand-border rounded-full p-0.5">
            <button onClick={() => setView('week')} className={`px-3 py-1 rounded-full text-[11px] font-medium ${view === 'week' ? 'bg-brand-green text-white' : 'text-brand-muted'}`} data-testid="view-week-btn">Semana</button>
            <button onClick={() => setView('month')} className={`px-3 py-1 rounded-full text-[11px] font-medium ${view === 'month' ? 'bg-brand-green text-white' : 'text-brand-muted'}`} data-testid="view-month-btn">Mês</button>
          </div>
          <button onClick={goToday} className="border border-brand-border text-brand-muted rounded-full px-3 py-1.5 text-xs hover:bg-brand-bg" data-testid="calendar-today-btn">Hoje</button>
          <button onClick={() => setReserveOpen(true)} className="border border-brand-border text-brand-green rounded-full px-3 py-1.5 text-xs font-medium flex items-center gap-1" data-testid="reserve-slot-btn"><Clock size={14} /> Reservar horário</button>
          {isAdmin && <button onClick={() => setConflictsOpen(true)} className="border border-brand-border text-amber-700 rounded-full px-3 py-1.5 text-xs font-medium flex items-center gap-1" data-testid="conflict-report-btn"><Warning size={14} /> Conflitos</button>}
          <button onClick={() => openAdd()} className="bg-brand-green text-white rounded-full px-4 py-1.5 text-xs font-medium flex items-center gap-1" data-testid="calendar-new-btn"><Plus size={14} /> Novo</button>
        </div>
      </div>

      {/* Month view */}
      {view === 'month' && (
        <div className="bg-white border border-brand-border rounded-2xl overflow-hidden" data-testid="calendar-month-grid">
          <div className="grid grid-cols-7 border-b border-brand-border">
            {DAY_NAMES.slice(1).concat(DAY_NAMES[0]).map((dn, i) => (
              <div key={i} className="py-2 text-center border-r border-brand-border last:border-r-0">
                <p className="text-[10px] text-brand-muted uppercase">{dn}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {monthGrid.map((d, i) => {
              const ds = fmtDate(d);
              const items = dayItems(ds);
              const isToday = ds === todayStr;
              const inMonth = d.getMonth() === currentDate.getMonth();
              const visible = items.slice(0, 3);
              const remaining = items.length - visible.length;
              return (
                <div key={i} className={`border-r border-b border-brand-border last:border-r-0 p-1 min-h-[80px] cursor-pointer hover:bg-brand-bg/40 ${isToday ? 'bg-brand-green/5' : ''} ${!inMonth ? 'opacity-40' : ''}`} onClick={() => { setCurrentDate(new Date(d)); setView('week'); }} data-testid={`month-day-${ds}`}>
                  <p className={`text-[10px] font-semibold mb-1 ${isToday ? 'text-brand-green' : 'text-brand-text'}`}>{d.getDate()}</p>
                  <div className="space-y-0.5">
                    {visible.map(item => (
                      <div key={item.id} className="rounded px-1 truncate" style={{ backgroundColor: item._color + '15', borderLeft: `2px solid ${item._color}` }}>
                        <p className={`text-[8px] truncate ${item.status === 'cancelled' ? 'line-through' : ''}`} style={{ color: item._color }}>{item._label || (item._type === 'apt' ? item.patient_name : item.title)}</p>
                      </div>
                    ))}
                    {remaining > 0 && <p className="text-[8px] text-brand-muted px-1">+{remaining}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Week grid */}
      {view === 'week' && (
      <div className="bg-white border border-brand-border rounded-2xl overflow-hidden">
        {/* Day headers */}
        <div className="grid grid-cols-7 border-b border-brand-border">
          {weekDates.map((d, i) => {
            const ds = fmtDate(d);
            const isToday = ds === todayStr;
            return (
              <div key={i} className={`py-2 text-center border-r border-brand-border last:border-r-0 ${isToday ? 'bg-brand-green/5' : ''}`}>
                <p className="text-[10px] text-brand-muted uppercase">{DAY_NAMES[d.getDay()]}</p>
                <p className={`text-sm font-heading font-semibold ${isToday ? 'text-brand-green' : 'text-brand-text'}`}>{d.getDate()}</p>
              </div>
            );
          })}
        </div>
        {/* Day contents */}
        <div className="grid grid-cols-7 min-h-[400px]">
          {weekDates.map((d, i) => {
            const ds = fmtDate(d);
            const items = dayItems(ds);
            const isToday = ds === todayStr;
            return (
              <div key={i} className={`border-r border-brand-border last:border-r-0 p-1 min-h-[120px] ${isToday ? 'bg-brand-green/3' : ''}`} onClick={() => openAdd(ds)}>
                <div className="space-y-1">
                  {items.map(item => (
                    <div key={item.id} data-slot={`${item.date}|${item.time}`} className={`group rounded-lg px-1.5 py-1 cursor-pointer hover:opacity-80 ${focusApt && ((focusApt.id && focusApt.id === item.id) || (!focusApt.id && focusApt.date === item.date && focusApt.time === item.time)) ? 'ring-2 ring-red-400 animate-pulse' : ''}`} style={{ backgroundColor: item._color + '15', borderLeft: `3px solid ${item._color}`, ...(item._type === 'hold' ? { border: `1.5px dashed ${item._color}`, backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 4px, ${item._color}12 4px, ${item._color}12 8px)` } : {}) }}
                      onClick={(e) => { e.stopPropagation(); if (item._type === 'apt') openEditApt(item); else if (item._type === 'hold') setHoldDetail(item); }}
                      data-testid={item._type === 'apt' ? `apt-${item.id}` : item._type === 'hold' ? `hold-${item.id}` : `evt-${item.id}`}>
                      <div className="flex items-center justify-between">
                        <p className={`text-[9px] font-medium truncate ${item.status === 'cancelled' ? 'line-through' : ''}`} style={{ color: item._color }}>{item._label}</p>
                        <button onClick={(e) => { e.stopPropagation(); if (item._type === 'hold') setHoldDetail(item); else if (item._type === 'apt') deleteApt(item.id); else deleteEvent(item.id); }} className="hidden group-hover:block text-brand-placeholder hover:text-red-500"><Trash size={10} /></button>
                      </div>
                      {item._type === 'apt' && item._statusLabel && (
                        <p className="text-[8px] font-semibold mt-0.5" style={{ color: item._color }}>{item._statusLabel}</p>
                      )}
                      {item._type === 'apt' && item.type && !item._statusLabel && <p className="text-[8px] text-brand-muted">{item.type} {item.duration ? `(${item.duration}min)` : ''}</p>}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      )}

      {/* Add event/appointment dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">{editingAptId ? 'Editar consulta' : 'Novo evento'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {editingAptId && (() => { const a = appointments.find(x => x.id === editingAptId); return a?.needs_review ? (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-3 text-xs text-red-700 flex items-start justify-between gap-2" data-testid="gcal-review-banner">
                <span>Este evento foi cancelado no Google Calendar. A marcação não foi apagada, decide se queres cancelar, remarcar ou manter.</span>
                <button type="button" onClick={() => dismissGcalReview(editingAptId)} className="text-red-700 underline whitespace-nowrap shrink-0" data-testid="dismiss-gcal-review-btn">Dispensar</button>
              </div>
            ) : null; })()}
            {!editingAptId && (
              <div className="flex gap-2">
                <button onClick={() => setAddType('consulta')} className={`flex-1 py-2 rounded-xl text-xs font-medium ${addType === 'consulta' ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>Consulta</button>
                <button onClick={() => setAddType('evento')} className={`flex-1 py-2 rounded-xl text-xs font-medium ${addType === 'evento' ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>Evento</button>
                <button onClick={() => setAddType('bloco')} className={`flex-1 py-2 rounded-xl text-xs font-medium ${addType === 'bloco' ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>Bloco</button>
              </div>
            )}
            {addType === 'consulta' && (
              <div><Label className="text-xs text-brand-muted mb-1 block">Paciente</Label>
                <PatientSearch value={form.patient_id} onChange={v => setForm({ ...form, patient_id: v })} />
              </div>
            )}
            {addType === 'consulta' && isAdmin && (
              <div>
                <Label className="text-xs text-brand-muted mb-1 block">Nutricionista responsável</Label>
                <select value={form.nutritionist_id || ''} onChange={e => setForm({ ...form, nutritionist_id: e.target.value })} className="w-full border border-brand-border rounded-xl px-3 py-2 text-sm bg-white" data-testid="apt-nutritionist">
                  <option value={user?.user_id || ''}>{(user?.name || 'Eu') + ' (eu)'}</option>
                  {team.filter(t => t.user_id !== user?.user_id).map(t => <option key={t.user_id} value={t.user_id}>{t.name || t.email}</option>)}
                </select>
              </div>
            )}
            {addType !== 'consulta' && (
              <div><Label className="text-xs text-brand-muted mb-1 block">Título</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Ex: Reunião de equipa" className="rounded-xl border-brand-border" /></div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs text-brand-muted mb-1 block">Data</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="rounded-xl border-brand-border" data-testid="apt-form-date" /></div>
              <div><Label className="text-xs text-brand-muted mb-1 block">Hora</Label><Input type="time" value={form.time} onChange={e => setForm({ ...form, time: e.target.value })} className="rounded-xl border-brand-border" data-testid="apt-form-time" /></div>
            </div>
            {addType === 'consulta' && (
              <div><Label className="text-xs text-brand-muted mb-1 block">Duração (min)</Label><Input type="number" value={form.duration} onChange={e => setForm({ ...form, duration: e.target.value })} className="rounded-xl border-brand-border w-24" /></div>
            )}
            {editingAptId && addType === 'consulta' && (
              <div><Label className="text-xs text-brand-muted mb-1 block">Tipo</Label>
                <div className="flex gap-2">
                  {['presencial', 'online'].map(t => <button key={t} type="button" onClick={() => setForm({ ...form, type: t, link: t === 'presencial' ? '' : form.link })} className={`flex-1 px-3 py-2 rounded-xl text-xs font-medium capitalize ${form.type === t ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`} data-testid={`apt-edit-type-${t}`}>{t}</button>)}
                </div>
              </div>
            )}
            {editingAptId && addType === 'consulta' && form.type === 'online' && (
              <div>
                <Label className="text-xs text-brand-muted mb-1 block">Link da videochamada</Label>
                <div className="flex gap-2">
                  <Input value={form.link} onChange={e => setForm({ ...form, link: e.target.value })} placeholder="https://meet.google.com/..." className="rounded-xl border-brand-border flex-1" data-testid="apt-edit-link" />
                  <button type="button" disabled={!form.link} onClick={() => { navigator.clipboard.writeText(form.link); toast.success('Link copiado'); }} className="px-3 rounded-xl border border-brand-border text-brand-muted hover:text-brand-green disabled:opacity-40" data-testid="apt-edit-link-copy" title="Copiar"><Copy size={16} /></button>
                  <button type="button" disabled={!form.link} onClick={() => window.open(form.link, '_blank')} className="px-3 rounded-xl border border-brand-border text-brand-muted hover:text-brand-green disabled:opacity-40" data-testid="apt-edit-link-open" title="Abrir"><ArrowSquareOut size={16} /></button>
                </div>
                {form.link && !/^https:\/\//i.test(form.link) && (
                  <p className="text-[11px] text-red-600 mt-1" data-testid="apt-edit-link-error">O link tem de começar por https://</p>
                )}
                {form.link && /^https:\/\//i.test(form.link) && !/meet\.google\.com/i.test(form.link) && (
                  <p className="text-[11px] text-amber-600 mt-1" data-testid="apt-edit-link-warning">Este link não parece ser do Google Meet, confirma que está correto.</p>
                )}
              </div>
            )}
            {addType !== 'consulta' && (
              <div className="flex items-center gap-2">
                <Label className="text-xs text-brand-muted">Cor:</Label>
                <input type="color" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} className="w-8 h-8 rounded border-0" />
              </div>
            )}
            <div><Label className="text-xs text-brand-muted mb-1 block">Notas</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} className="rounded-xl border-brand-border resize-none" /></div>
            {/* Tags */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs text-brand-muted">Tags</Label>
                <button type="button" onClick={() => setShowTagMgmt(true)} className="text-[9px] text-brand-green hover:underline">Gerir tags</button>
              </div>
              <div className="flex flex-wrap gap-1">
                {allTags.filter(t => t.type === 'custom').map(t => {
                  const sel = (form.tags || []).includes(t.id);
                  return <button key={t.id} type="button" onClick={() => setForm({ ...form, tags: sel ? form.tags.filter(x => x !== t.id) : [...(form.tags || []), t.id] })} className={`px-2 py-0.5 rounded-full text-[9px] border ${sel ? 'border-brand-green bg-brand-green/10' : 'border-brand-border'}`} style={sel ? { color: t.color } : {}}>{t.name}</button>;
                })}
              </div>
            </div>
            <button onClick={saveEvent} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="apt-form-save">Guardar</button>
            {editingAptId && (
              <div className="flex flex-wrap gap-2 pt-1">
                {(() => { const a = appointments.find(x => x.id === editingAptId); return a?.whatsapp_url ? (
                  <WhatsAppConfirmButton url={a.whatsapp_url} className="flex-1 !py-2 !text-xs" testId="apt-whatsapp-btn" />
                ) : null; })()}
                <button onClick={() => { const a = appointments.find(x => x.id === editingAptId); setShowAdd(false); setShowReschedule({ id: editingAptId, patient_name: a?.patient_name || '' }); setReschedForm({ new_date: '', new_time: '09:00', reason: '' }); }} className="flex-1 border border-amber-500 text-amber-600 rounded-full py-2 text-xs font-medium hover:bg-amber-50" data-testid="apt-reschedule-btn">Remarcar</button>
                {(() => { const a = appointments.find(x => x.id === editingAptId); const isFalta = a?.status === 'falta'; return (
                  <button onClick={() => markNoShow(editingAptId, isFalta)} className={`flex-1 border rounded-full py-2 text-xs font-medium ${isFalta ? 'border-brand-green text-brand-green hover:bg-brand-green/5' : 'border-red-600 text-red-600 hover:bg-red-50'}`} data-testid="apt-noshow-btn">{isFalta ? 'Reverter falta' : 'Faltou'}</button>
                ); })()}
                <button onClick={() => { const a = appointments.find(x => x.id === editingAptId); setShowAdd(false); setShowCancel({ id: editingAptId, patient_name: a?.patient_name || '' }); setCancelReason(''); setCancelMessage(''); }} className="flex-1 border border-red-400 text-red-500 rounded-full py-2 text-xs font-medium hover:bg-red-50" data-testid="apt-cancel-btn">Cancelar consulta</button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel dialog */}
      <Dialog open={!!showCancel} onOpenChange={(o) => !o && setShowCancel(null)}>
        <DialogContent className="rounded-3xl border-brand-border" data-testid="cancel-dialog">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Cancelar consulta</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {showCancel?.patient_name && <p className="text-xs text-brand-muted">Paciente: <span className="text-brand-text font-medium">{showCancel.patient_name}</span></p>}
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Motivo interno <span className="text-red-500">*</span></Label>
              <Textarea value={cancelReason} onChange={e => setCancelReason(e.target.value)} rows={3} placeholder="Só aparece no histórico da ficha, a paciente não vê" className="rounded-xl border-brand-border resize-none" data-testid="cancel-reason-input" />
            </div>
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Mensagem para a paciente (opcional)</Label>
              <Textarea value={cancelMessage} onChange={e => setCancelMessage(e.target.value)} rows={2} placeholder="Aparece no email de cancelamento, se escreveres alguma coisa" className="rounded-xl border-brand-border resize-none" data-testid="cancel-patient-message-input" />
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowCancel(null)} className="flex-1 border border-brand-border text-brand-muted rounded-full py-2.5 text-sm">Voltar</button>
              <button onClick={submitCancel} className="flex-1 bg-red-500 text-white rounded-full py-2.5 text-sm font-medium" data-testid="cancel-confirm-btn">Confirmar cancelamento</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reschedule dialog */}
      <Dialog open={!!showReschedule} onOpenChange={(o) => !o && setShowReschedule(null)}>
        <DialogContent className="rounded-3xl border-brand-border" data-testid="reschedule-dialog">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Remarcar consulta</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {showReschedule?.patient_name && <p className="text-xs text-brand-muted">Paciente: <span className="text-brand-text font-medium">{showReschedule.patient_name}</span></p>}
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs text-brand-muted mb-1 block">Nova data</Label><Input type="date" value={reschedForm.new_date} onChange={e => setReschedForm({ ...reschedForm, new_date: e.target.value })} className="rounded-xl border-brand-border" data-testid="resched-date-input" /></div>
              <div><Label className="text-xs text-brand-muted mb-1 block">Nova hora</Label><Input type="time" value={reschedForm.new_time} onChange={e => setReschedForm({ ...reschedForm, new_time: e.target.value })} className="rounded-xl border-brand-border" data-testid="resched-time-input" /></div>
            </div>
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Motivo <span className="text-red-500">*</span></Label>
              <Textarea value={reschedForm.reason} onChange={e => setReschedForm({ ...reschedForm, reason: e.target.value })} rows={2} placeholder="Indica o motivo da remarcação" className="rounded-xl border-brand-border resize-none" data-testid="resched-reason-input" />
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowReschedule(null)} className="flex-1 border border-brand-border text-brand-muted rounded-full py-2.5 text-sm">Voltar</button>
              <button onClick={submitReschedule} className="flex-1 bg-amber-500 text-white rounded-full py-2.5 text-sm font-medium" data-testid="resched-confirm-btn">Confirmar remarcação</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* PROMPT 3 — Item 3: Modal de matches da lista de espera */}
      <Dialog open={!!waitlistMatches} onOpenChange={(o) => !o && setWaitlistMatches(null)}>
        <DialogContent className="rounded-3xl border-brand-border max-w-xl" data-testid="waitlist-matches-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-brand-text">Candidatas da lista de espera</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {waitlistMatches && (
              <p className="text-xs text-brand-muted">
                Slot livre: <b>{fmtDateTime(waitlistMatches.date, waitlistMatches.time)}</b> · {waitlistMatches.day_of_week} · {waitlistMatches.period === 'manha' ? 'manhã' : waitlistMatches.period}
                {waitlistMatches.modality && waitlistMatches.modality !== 'indiferente' && ` · ${waitlistMatches.modality}`}
                {waitlistMatches.program && ` · ${waitlistMatches.program}`}
              </p>
            )}
            {matchesLoading ? (
              <p className="text-center text-sm text-brand-muted py-4">A procurar candidatas…</p>
            ) : (waitlistMatches?.matches?.length || 0) === 0 ? (
              <p className="text-center text-sm text-brand-muted py-4" data-testid="waitlist-matches-empty">Nenhuma candidata com match exato.</p>
            ) : (
              <div className="space-y-2">
                {waitlistMatches.matches.map(c => {
                  const waText = encodeURIComponent(`Olá ${c.name?.split(' ')[0] || ''}, tenho disponibilidade para ${waitlistMatches.date}${waitlistMatches.time ? ` às ${waitlistMatches.time}` : ''}. Confirmas?`);
                  const tel = (c.phone || '').replace(/[^0-9+]/g, '');
                  const waUrl = tel ? `https://wa.me/${tel.replace(/^\+/, '')}?text=${waText}` : null;
                  return (
                    <div key={c.id} className="bg-white border border-brand-border rounded-xl p-3" data-testid={`waitlist-match-${c.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-brand-text">{c.name}</p>
                          <p className="text-[11px] text-brand-muted">{c.program} · entrada: {fmtPtDate(c.created_at?.slice(0, 10))}</p>
                          {c.phone && <p className="text-[11px] text-brand-muted">{c.phone}</p>}
                        </div>
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-brand-mint/20 text-brand-green whitespace-nowrap">{c.status}</span>
                      </div>
                      <div className="flex gap-2 mt-2">
                        {waUrl && (
                          <a href={waUrl} target="_blank" rel="noreferrer" className="flex-1 bg-emerald-100 text-emerald-700 rounded-full py-1.5 text-[11px] font-medium text-center hover:bg-emerald-200" data-testid={`waitlist-wa-${c.id}`}>WhatsApp</a>
                        )}
                        <button
                          onClick={async () => {
                            try {
                              await axios.post(`${API}/api/admin/waitlist/schedule`, { waitlist_id: c.id, date: waitlistMatches.date, time: waitlistMatches.time });
                              toast.success(`${c.name} agendada e removida da lista`);
                              setWaitlistMatches(null); fetch();
                            } catch (err) { toast.error(err.response?.data?.detail || 'Erro a agendar'); }
                          }}
                          className="flex-1 bg-brand-green text-white rounded-full py-1.5 text-[11px] font-medium"
                          data-testid={`waitlist-schedule-${c.id}`}
                        >Agendar diretamente</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <button onClick={() => setWaitlistMatches(null)} className="w-full border border-brand-border text-brand-muted rounded-full py-2 text-sm" data-testid="waitlist-matches-close">Fechar</button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Tag management */}
      <Dialog open={showTagMgmt} onOpenChange={setShowTagMgmt}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Gerir tags</DialogTitle></DialogHeader>
          <div className="space-y-2 mb-3">
            {allTags.filter(t => t.type === 'auto').map(t => (
              <div key={t.id} className="flex items-center gap-2 text-xs text-brand-muted"><div className="w-3 h-3 rounded-full" style={{ backgroundColor: t.color }} />{t.name} <span className="text-[8px]">(auto)</span></div>
            ))}
            {allTags.filter(t => t.type === 'custom').map(t => (
              <div key={t.id} className="flex items-center gap-2 bg-brand-bg/30 rounded-lg px-2 py-1.5">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: t.color }} />
                <span className="text-xs text-brand-text flex-1">{t.name}</span>
                <button onClick={async () => { const ok = await confirmDelete({ description: `Vai apagar a tag "${t.name}".` }); if (!ok) return; await axios.delete(`${API}/api/calendar-tags/${t.id}`); fetch(); }} className="text-brand-placeholder hover:text-red-500"><Trash size={12} /></button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Input value={newTag.name} onChange={e => setNewTag({ ...newTag, name: e.target.value })} placeholder="Nova tag" className="flex-1 rounded-lg border-brand-border text-sm h-9" />
            <input type="color" value={newTag.color} onChange={e => setNewTag({ ...newTag, color: e.target.value })} className="w-9 h-9 rounded border-0" />
            <button onClick={async () => { if (!newTag.name.trim()) return; await axios.post(`${API}/api/calendar-tags`, newTag); setNewTag({ name: '', color: '#657161' }); fetch(); toast.success('Tag criada'); }} className="bg-brand-green text-white rounded-lg px-3 h-9 text-xs"><Plus size={14} /></button>
          </div>
        </DialogContent>
      </Dialog>
      {conflictsOpen && <ConflictReportDialog open onClose={() => setConflictsOpen(false)} />}
      {reserveOpen && <ReserveSlotDialog open onClose={() => setReserveOpen(false)} onSaved={() => { setReserveOpen(false); fetch(); }} isAdmin={isAdmin} team={team} user={user} defaultDate={fmtDate(currentDate)} />}
      {holdDetail && <HoldDetailDialog hold={holdDetail} clients={clients} onClose={() => setHoldDetail(null)} onChanged={() => { setHoldDetail(null); fetch(); }} />}
    </div>
  );
}
